import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, Link, Navigate, useLocation } from "react-router-dom";
import { UserButton, useUser } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LayoutDashboard, Bot, BookOpen, Youtube, Radio, BarChart3, CreditCard, Settings, Menu, OctagonX, ShieldCheck, EyeOff } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { AppErrorBoundary } from "@/components/AppState";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useApi, useQ, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { LangToggle, LiveBadge } from "@/components/common";

const NAV = [
  { to: "/dashboard", key: "nav_overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/agents", key: "nav_agents", icon: Bot, page: "agents" },
  { to: "/dashboard/knowledge", key: "nav_knowledge", icon: BookOpen, page: "knowledge" },
  { to: "/dashboard/channels", key: "nav_channels", icon: Youtube, page: "channels" },
  { to: "/dashboard/youtube", key: "nav_youtube", icon: Radio, page: "youtube" },
  { to: "/dashboard/streams", key: "nav_streams", icon: Radio, page: "streams" },
  { to: "/dashboard/analytics", key: "nav_analytics", icon: BarChart3, page: "analytics" },
  { to: "/dashboard/billing", key: "nav_billing", icon: CreditCard, page: "billing" },
  { to: "/dashboard/settings", key: "nav_settings", icon: Settings, page: "settings" },
];
const ADMIN_NAV = { to: "/dashboard/admin", key: "nav_admin", icon: ShieldCheck };

export const useIsAdmin = () => useQ("/admin/me", { staleTime: 0, refetchInterval: 30000, retry: false }).data?.role === "admin";

const useNavItems = () => {
  const { site } = useT();
  const isAdmin = useIsAdmin();
  const pages = site?.pages || {};
  const items = NAV.filter((n) => !n.page || pages[n.page] !== false || isAdmin)
    .map((n) => ({ ...n, hiddenForUsers: n.page && pages[n.page] === false }));
  return isAdmin ? [...items, ADMIN_NAV] : items;
};

const Brand = () => (
  <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
    <span className="h-7 w-7 rounded-md bg-indigo-600 flex items-center justify-center font-display font-black text-sm">IA</span>
    <span className="hidden sm:inline font-display font-extrabold tracking-tight">IAGORA <span className="text-indigo-400">AI LIVE</span></span>
  </Link>
);

const NavList = ({ onNavigate }) => {
  const { t } = useT();
  const items = useNavItems();
  return (
    <nav className="flex flex-col gap-1">
      {items.map(({ to, key, icon: Icon, end, hiddenForUsers }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${key}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-indigo-600/15 text-white border border-indigo-500/30" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"}`}>
          <Icon className="h-4 w-4" /> {t(key)}
          {hiddenForUsers && <EyeOff className="h-3.5 w-3.5 ml-auto text-amber-400" title={t("hidden_for_users")} />}
        </NavLink>
      ))}
    </nav>
  );
};

const PageGuard = () => {
  const { site, siteError, refreshSite, t } = useT();
  const { pathname } = useLocation();
  const { data: me, error, refetch } = useQ("/admin/me", { staleTime: 0, refetchInterval: 30000, retry: false });
  const page = pathname.split("/")[2];
  useEffect(() => { refreshSite(); }, [pathname, refreshSite]);
  if (error || (siteError && !site)) return <div role="alert" data-testid="page-access-error" className="text-sm text-rose-300">{t(error ? "access_check_error" : "site_load_error")}<button data-testid="page-access-retry" className="ml-3 underline" onClick={() => { refetch(); refreshSite(); }}>{t("retry")}</button></div>;
  if (!site || !me) return <p className="text-sm text-slate-400 animate-pulse" data-testid="page-access-loading">{t("loading")}</p>;
  if (page && site.pages?.[page] === false && me.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <AppErrorBoundary resetKey={pathname}><Outlet /></AppErrorBoundary>;
};

const EmergencyStop = () => {
  const { t } = useT();
  const api = useApi();
  const qc = useQueryClient();
  const run = async () => {
    try {
      const { data } = await api.post("/emergency-stop");
      toast.success(`${t("emergency_done")} (${data.stopped})`);
      qc.invalidateQueries();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid="emergency-stop-btn" className="inline-flex items-center gap-2 rounded-full border border-rose-500/40 bg-rose-600/10 px-3 py-1.5 text-xs font-semibold text-rose-300 hover:bg-rose-600/25 transition-colors">
          <OctagonX className="h-4 w-4" /> <span className="hidden sm:inline">{t("emergency_stop")}</span>
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#111118] border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">{t("emergency_title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("emergency_desc")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="emergency-cancel-btn">{t("cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={run} className="bg-rose-600 hover:bg-rose-500" data-testid="emergency-confirm-btn">{t("stop_all")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const useProfileSync = () => {
  const { user } = useUser();
  const api = useApi();
  const { lang } = useT();
  const done = useRef(null);
  useEffect(() => {
    if (!user || done.current === user.id) return;
    done.current = user.id;
    api.patch("/me", { name: user.fullName || "", email: user.primaryEmailAddress?.emailAddress || "", language: lang }).catch(() => {});
  }, [user, api, lang]);
};

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const { data: ov } = useQ("/overview", { refetchInterval: 10000 });
  useProfileSync();
  return (
    <div className="min-h-screen bg-[#09090B] text-slate-50">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col gap-8 border-r border-white/[0.08] bg-[#0B0B10] p-5">
        <Brand />
        <NavList />
        <p className="mt-auto text-[11px] text-slate-600 font-mono-ui">Phase 1 · v0.1</p>
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-white/[0.08] bg-zinc-950/70 backdrop-blur-xl px-4 sm:px-8 h-16">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 rounded-md hover:bg-white/5" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#0B0B10] border-white/10 w-72 p-5 flex flex-col gap-8" aria-describedby={undefined}>
                <SheetTitle className="sr-only">IAGORA AI LIVE — Menu</SheetTitle>
                <Brand />
                <NavList onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="lg:hidden"><Brand /></div>
            <div className="hidden lg:block"><LiveBadge live={!!ov?.live_stream} /></div>
          </div>
          <div className="flex items-center gap-3">
            <EmergencyStop />
            <LangToggle />
            <UserButton />
          </div>
        </header>
        <main className="px-4 sm:px-8 py-8 max-w-[1400px]">
          <PageGuard />
        </main>
      </div>
    </div>
  );
}
